// =============================================================================
// ui/tabs/AnalyticsExcursion.tsx — the Excursion sub-tab.
// -----------------------------------------------------------------------------
// ── 🚨 SCOPED TO ONE INSTRUMENT, AND THAT IS NOT A PREFERENCE ──
//
// MAE and MFE are stored in POINTS. The old client's own comment:
//
//   "Layer 1 instrument filter: aggregating MAE/MFE across instruments is
//    statistically meaningless (1.5 pts on CL ≠ 1.5 pts on ES)."
//
// So the instrument selector is the FIRST control on the page, it defaults to the
// most-traded instrument in the period rather than to "all", and every figure
// below it is scoped by it. `All instruments` exists as a legacy aggregate and is
// labelled as one — a user who picks it should know the numbers mix units.
//
// ⚠ Same period rules as Overview: Analytics has its OWN period and ignores the
// global date filter (see selectors/analytics.ts). The read is unbounded.
//
// ── COMPLETE — eleven of eleven ──
//
// ⚠ THIS HEADER SAID EIGHT CARDS FOR WEEKS and the tab's own honesty card
// inherited the figure. Nobody had counted them; the number was copied from
// comment to comment. **The old client has ELEVEN**, in three sections:
//
//   Entry edge      E-Ratio · MAE vs stop distance · Temporal profile
//   Exit skill      MFE Capture · Hold-duration edge · Cost of give-back ·
//                   Cost of premature exit · Trade extension
//   Diagnose        Loser asymmetry · Stop / target calibration · R-multiple
//
// All eleven are here. Give-back and premature exit share ONE card on purpose —
// opposite habits, and the value is knowing which dominates (see `CostCard`) —
// so there are ten card containers for eleven measures, and a test counts them.
//
// ⚠ What is missing now is a BEHAVIOUR, not a measure: computing a post-exit
// walk (metered, runs in the old client), clicking a row to open a setup, and
// the `?` explainers. Say that, and re-read the honesty card whenever something
// ships — it is the one piece of copy whose accuracy decreases as work goes well.
// =============================================================================

import { useMemo, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { useFilters } from '../state/filters';
import { usePreferences } from '../state/preferences';
import { useRange } from '../state/range';
import { useTradeRows } from '../state/useTradeRows';
import {
  ANALYTICS_PERIODS, DEFAULT_ANALYTICS_PERIOD, analyticsPeriodLabel,
  dateInAnalyticsPeriod, type AnalyticsPeriod,
} from '../../selectors/analytics';
import {
  FAMILY_PREFIX, buildInstrumentFamilies, captureStats, defaultInstrument,
  instrumentMatches, instrumentsByVolume, stopCalibration,
  type ExcursionTrade,
} from '../../selectors/excursionStats';
import {
  durationLabel, eratio, maeStopRatio, temporalProfile,
  dominantExitHabit, giveBackCost, holdDurationEdge, prematureExitCost,
  loserAsymmetry, rMultipleProfile,
  extensionProfile,
  MAE_STOP_BANDS, EXTENSION_WINDOWS, EXTENSION_POSITIVE_FROM, EXIT_REASON_LABELS,
  DEFAULT_EXTENSION_WINDOW, ASYMMETRY_MIN_LOSERS, RMULT_MIN_WINNERS,
  type GroupBy, type EratioResult, type MaeStopRow, type TemporalRow,
  type ExitTrade, type GiveBackCost, type HoldDurationEdge, type PrematureExitCost,
  type DiagnoseTrade, type LoserAsymmetry, type RMultipleProfile,
  type ExitReasonKey, type ExtensionCardTrade, type ExtensionLens,
  type ExtensionMode, type ExtensionProfile,
} from '../../selectors/excursionStats';
import { useContracts } from '../state/contracts';
import { usePnlUnit } from '../state/usePnlUnit';
import type { PnlUnit } from '../../selectors/pnlUnit';
import { tradesCountingForMetrics } from '../../selectors/accounts';
import { effectiveTradeDate } from '../../selectors/population';
import { usePatterns } from '../state/patterns';
import { useExitModels } from '../state/exitModels';
import { money, num, pct, unitCompact } from '../format';

const UNBOUNDED = { startDate: null, endDate: null };
const ALL = 'all';

export function AnalyticsExcursionTab() {
  const { accounts } = useAccounts();
  // ⚠ `facets` deliberately NOT read — see the population memo below.
  const { accounts: selection } = useFilters();
  const { prefs } = usePreferences();
  // ⚠ Hoisted above the instrument-family memo, which needs the catalogue to
  // check that a mini and its micro really do share a tick size.
  const { contracts } = useContracts();
  // Points are arguably the NATURAL unit on this tab — every figure here is a
  // price excursion. The two cost functions take the unit now (#44).
  const { unit } = usePnlUnit();
  const { today } = useRange();
  const mode = prefs?.dayBoundaryMode ?? null;

  const [period, setPeriod] = useState<AnalyticsPeriod>(DEFAULT_ANALYTICS_PERIOD);
  // ⚠ NULL until the data arrives, then defaulted to the most-traded instrument
  // — not to `all`. Once the user picks anything, including `all`, their choice
  // stands across period changes, matching the old client.
  const [instrument, setInstrument] = useState<string | null>(null);

  const { phase, rows, error } = useTradeRows(UNBOUNDED, mode);

  // ⚠ Same population as every other analytics surface: account selection plus
  // exclude-from-metrics, and NEITHER the global date range nor the facets. The
  // first version applied the facets (beta does not) and omitted
  // `countsForMetrics` (beta applies it), so SIM and replay trades were shaping
  // the MAE/MFE distributions.
  const inPeriod = useMemo(() => {
    if (!rows || !mode || !today) return null;
    const scoped = tradesCountingForMetrics(rows, selection, accounts ?? []);
    return scoped.filter((t) => dateInAnalyticsPeriod(effectiveTradeDate(t, mode), period, today));
  }, [rows, mode, today, selection, accounts, period]);

  const asExcursion = useMemo<ExcursionTrade[] | null>(
    () => inPeriod?.map((t) => ({
      contract: t.contract as string | null,
      maePoints: t.maePoints, mfePoints: t.mfePoints,
      points: t.points, riskPoints: t.riskPoints, netPnl: t.netPnl,
    })) ?? null,
    [inPeriod],
  );

  const instruments = useMemo(
    () => (asExcursion ? instrumentsByVolume(asExcursion) : []),
    [asExcursion],
  );

  /**
   * Mini + micro pairs worth offering — issue #17, Josh's own request.
   *
   * ⚠ Only where BOTH have been traded in this period AND the catalogue says
   * their tick sizes match. Excursion is measured in points, and 10 points of
   * heat is the same market move on ES as on MES — which is exactly why this
   * grouping is sound where "All instruments" is not.
   */
  const families = useMemo(
    () => buildInstrumentFamilies(instruments, contracts ?? []),
    [instruments, contracts],
  );

  const active = instrument ?? (asExcursion ? defaultInstrument(asExcursion) : ALL);
  /** The family currently selected, if the selection is one. */
  const activeFamily = useMemo(
    () => (active.startsWith(FAMILY_PREFIX) ? families.find((f) => f.key === active) ?? null : null),
    [active, families],
  );

  const scoped = useMemo(() => {
    if (!asExcursion) return null;
    // ⚠ THE SCOPING. Every figure below reads this, never `asExcursion`.
    return active === ALL
      ? asExcursion
      : asExcursion.filter((t) => instrumentMatches(t.contract, active, families));
  }, [asExcursion, active, families]);

  const capture = useMemo(() => (scoped ? captureStats(scoped) : null), [scoped]);
  const calib = useMemo(() => (scoped ? stopCalibration(scoped) : null), [scoped]);

  // ── Entry Edge ─────────────────────────────────────────────────────────────
  // ⚠ SCOPED TO THE SAME INSTRUMENT as everything above. These group by pattern,
  // so a row reading "ORB · E-Ratio 2.4" over a mixture of MES and MNQ would be a
  // confident number about nothing.
  const { patterns } = usePatterns();
  const { exitModels } = useExitModels();
  const [groupBy, setGroupBy] = useState<GroupBy>('pattern');

  /**
   * ⚠ RETURNS NULL FOR AN ID THAT NO LONGER RESOLVES, which drops the row. A
   * pattern the trader has since deleted would otherwise appear as a raw id —
   * and a row labelled `p_8f3a` is worse than no row.
   */
  const nameOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of patterns ?? []) map.set(String(p.id), String(p.name ?? ''));
    for (const m of exitModels ?? []) map.set(String(m.id), String(m.name ?? ''));
    return (id: string): string | null => map.get(id) ?? null;
  }, [patterns, exitModels]);

  /**
   * ⚠ ONE shape for Entry Edge AND Diagnose, carrying the one extra field the
   * second needs. Two memos over the same rows, zipped by index, would attach
   * one trade's realised R to another's excursions the moment either filter
   * changed — silently, and with every number still plausible.
   */
  const edgeTrades = useMemo<DiagnoseTrade[] | null>(() => {
    if (!inPeriod) return null;
    const rowsIn = active === ALL
      ? inPeriod
      : inPeriod.filter((t) => instrumentMatches(t.contract, active, families));
    return rowsIn.map((t) => ({
      patternId: (t.patternId as string | null) ?? null,
      exitModelId: (t.exitModelId as string | null) ?? null,
      maePoints: t.maePoints, mfePoints: t.mfePoints,
      maeSecs: t.maeSecs, mfeSecs: t.mfeSecs,
      riskPoints: t.riskPoints, netPnl: t.netPnl,
      // ⚠ For the P&L column only — it follows the unit switch, and points,
      // ticks and R each need a field that dollars does not. See `EdgeTrade`.
      contract: t.contract, points: t.points,
      // ⚠ READ, never re-derived from points ÷ risk. The stored figure is what
      // every other surface prints; a second derivation here would disagree on
      // any trade whose risk was edited after the fact.
      rrRealized: t.rrRealized,
    }));
  }, [inPeriod, active]);

  const edge = useMemo<EratioResult | null>(
    () => (edgeTrades ? eratio(edgeTrades, groupBy, nameOf, contracts ?? [], unit) : null),
    [edgeTrades, groupBy, nameOf, contracts, unit],
  );
  const stopBands = useMemo<MaeStopRow[] | null>(
    () => (edgeTrades ? maeStopRatio(edgeTrades, groupBy, nameOf) : null),
    [edgeTrades, groupBy, nameOf],
  );
  const tempo = useMemo<TemporalRow[] | null>(
    () => (edgeTrades ? temporalProfile(edgeTrades, groupBy, nameOf) : null),
    [edgeTrades, groupBy, nameOf],
  );

  // ── Exit skill ─────────────────────────────────────────────────────────────
  // ⚠ THE CONTRACT CATALOGUE IS ONLY FOR THE TWO COST CARDS. Everything else on
  // this tab is points or percentages; "what did this habit cost me" is the one
  // question that has to be in dollars, and dollars need $/point.

  const exitTrades = useMemo<ExitTrade[] | null>(() => {
    if (!inPeriod) return null;
    const rowsIn = active === ALL
      ? inPeriod
      : inPeriod.filter((t) => instrumentMatches(t.contract, active, families));
    return rowsIn.map((t) => ({
      patternId: (t.patternId as string | null) ?? null,
      exitModelId: (t.exitModelId as string | null) ?? null,
      contract: t.contract, qty: t.qty, side: t.side, entry: t.entry, exit: t.exit,
      timeIn: t.timeIn, timeOut: t.timeOut,
      mfePoints: t.mfePoints, mfeSecs: t.mfeSecs, points: t.points,
      netPnl: t.netPnl, grossPnl: t.grossPnl, fees: t.fees,
      riskPoints: t.riskPoints,
      // ⚠ WAS MISSING, and the cast below hid it from tsc. Every figure on this
      // card stated in R reads it — the hold table's average and give-back's
      // realised leg both go null without it.
      rrRealized: t.rrRealized,
      extensions: t.extensions ?? null,
      // 🚨 THE FILLS, and without them give-back silently reverts to the upper
      // bound this card used to report (#88). They were already in the read —
      // `executions:extras->executions` is in the table projection — and only
      // this mapper was dropping them.
      //
      // ✅ AND THE CAST IS GONE. This was `as unknown as ExitTrade`, which erases
      // the check entirely: that is how `rrRealized` went missing here once
      // already (#67) and how `executions` was missing until now. `satisfies`
      // verifies the shape and still infers it, so deleting any line in this
      // object is a BUILD ERROR — measured: removing this one produces
      // "Property 'executions' is missing … but required in type 'ExitTrade'".
      executions: t.executions ?? null,
    } satisfies ExitTrade));
  }, [inPeriod, active]);

  const hold = useMemo<HoldDurationEdge | null>(
    () => (exitTrades ? holdDurationEdge(exitTrades, contracts ?? [], unit) : null),
    [exitTrades, contracts, unit]);
  const giveBack = useMemo<GiveBackCost | null>(
    () => (exitTrades ? giveBackCost(exitTrades, contracts ?? [], unit) : null),
    [exitTrades, contracts, unit]);
  const premature = useMemo<PrematureExitCost | null>(
    () => (exitTrades ? prematureExitCost(exitTrades, contracts ?? [], groupBy, nameOf, unit) : null),
    [exitTrades, contracts, groupBy, nameOf, unit]);

  // ── Trade extension ────────────────────────────────────────────────────────
  // ⚠ FOUR CONTROLS, and each answers a different question rather than being a
  // preference: which window of time after the exit, which stop the run is
  // credited against, whether to normalise by risk, and which kind of exit.
  const [extWindow, setExtWindow] = useState<string>(DEFAULT_EXTENSION_WINDOW);
  const [extLens, setExtLens] = useState<ExtensionLens>('stop');
  const [extMode, setExtMode] = useState<ExtensionMode>('r');
  const [extReason, setExtReason] = useState<ExitReasonKey | null>(null);

  const extensionTrades = useMemo<ExtensionCardTrade[] | null>(() => {
    if (!inPeriod) return null;
    const rowsIn = active === ALL
      ? inPeriod
      : inPeriod.filter((t) => instrumentMatches(t.contract, active, families));
    return rowsIn.map((t) => ({
      patternId: (t.patternId as string | null) ?? null,
      contract: t.contract, side: t.side, entry: t.entry, exit: t.exit, qty: t.qty,
      riskPoints: t.riskPoints, plannedStop: t.plannedStop, timeOut: t.timeOut,
      extensions: t.extensions ?? null,
    } as unknown as ExtensionCardTrade));
  }, [inPeriod, active]);

  const extension = useMemo<ExtensionProfile | null>(() => (
    extensionTrades
      ? extensionProfile(
        extensionTrades,
        { window: extWindow, lens: extLens, mode: extMode, exitReason: extReason },
        nameOf,
      )
      : null
  ), [extensionTrades, extWindow, extLens, extMode, extReason, nameOf]);

  // ── Diagnose & calibrate ───────────────────────────────────────────────────
  const asymmetry = useMemo<LoserAsymmetry | null>(
    () => (edgeTrades ? loserAsymmetry(edgeTrades, groupBy, nameOf) : null),
    [edgeTrades, groupBy, nameOf]);
  const rMultiple = useMemo<RMultipleProfile | null>(
    () => (edgeTrades ? rMultipleProfile(edgeTrades, groupBy, nameOf) : null),
    [edgeTrades, groupBy, nameOf]);

  if (phase === 'failed') {
    return (
      <div className="card" role="alert">
        <h3>Couldn’t load your excursion analytics</h3>
        <p className="muted">Nothing has been changed — this is a read problem. {error}</p>
      </div>
    );
  }

  if (!scoped || !capture || !calib) return <p className="muted">Loading excursion analytics…</p>;

  return (
    <div className="analytics-tab">
      <div className="an-periodbar">
        {ANALYTICS_PERIODS.map((p) => (
          <button
            key={p.id} type="button"
            className={`period-chip${period.id === p.id ? ' active' : ''}`}
            aria-pressed={period.id === p.id}
            onClick={() => setPeriod({ id: p.id })}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ⚠ FIRST control on the page, deliberately. Every number below is only
          meaningful within one instrument. */}
      <div className="card an-instrument">
        <label className="range-picker-label" htmlFor="exc-instrument">Instrument</label>
        <select
          id="exc-instrument"
          value={active}
          onChange={(e) => setInstrument(e.target.value)}
        >
          {/* ⚠ ABOVE the singles, because a pair is the BETTER default reading
              when it exists — same market, twice the sample. Only offered when
              both have been traded this period and their tick sizes match. */}
          {families.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label} ({f.count} trade{f.count === 1 ? '' : 's'})
            </option>
          ))}
          {instruments.map((i) => (
            <option key={i.symbol} value={i.symbol}>
              {i.symbol} ({i.count} trade{i.count === 1 ? '' : 's'})
            </option>
          ))}
          {/* Labelled as the legacy aggregate it is. */}
          <option value={ALL}>All instruments — mixes units ({asExcursion?.length ?? 0})</option>
        </select>
        <span className="muted">
          {active === ALL
            ? '⚠ Mixing instruments: 1.5 points on CL is not 1.5 points on ES, so these medians are not comparable.'
            : activeFamily
              // ⚠ It says WHY this pair is legitimate where "All instruments" is
              // not. Without the reason, the two aggregate options look like the
              // same compromise offered twice.
              ? `Scoped to ${activeFamily.label} · ${analyticsPeriodLabel(period)} — same tick size, so points mean the same thing on both`
              : `Scoped to ${active} · ${analyticsPeriodLabel(period)}`}
        </span>
      </div>

      {scoped.length === 0 ? (
        <div className="card">
          <h3>No trades for {active === ALL ? 'this period' : (activeFamily?.label ?? active)}</h3>
          <p className="muted">Pick a wider period or another instrument.</p>
        </div>
      ) : (
        <>
          {/* ── ENTRY EDGE ───────────────────────────────────────────────────
              ⚠ FIRST, and that order is the old client's argument rather than a
              layout choice: these read what the market OFFERED, independent of
              how the trade was exited. If the entry has no edge here, nothing
              downstream can save it. */}
          <div className="section-header exc-divider">
            <h3>Entry edge</h3>
            <span className="muted">is the setup worth trading at all?</span>
          </div>
          <p className="muted exc-section-blurb">
            These read what the market <i>offered</i> you — room, heat and tempo —
            independent of how you exited.
          </p>

          <div className="exc-groupbar">
            {(['pattern', 'exit-model'] as const).map((g) => (
              <button
                key={g} type="button"
                className={`period-chip${groupBy === g ? ' active' : ''}`}
                aria-pressed={groupBy === g}
                onClick={() => setGroupBy(g)}
              >{g === 'pattern' ? 'By pattern' : 'By exit model'}</button>
            ))}
          </div>

          <EratioCard unit={unit} edge={edge} groupBy={groupBy} />
          <StopBandCard rows={stopBands} groupBy={groupBy} />
          <TempoCard rows={tempo} groupBy={groupBy} />

          <div className="section-header exc-divider">
            <h3>Exit skill</h3>
            <span className="muted">are you keeping what the setup gives you?</span>
          </div>
          <p className="muted exc-section-blurb">
            These compare what you <i>realised</i> against what was actually
            there — your capture, the dollars left on the table, and how far it
            ran after you walked away.
          </p>

          <div className="section-header">
            <h3>MFE Capture</h3>
            <span className="muted">how much of the favourable move you banked</span>
          </div>
          <div className="card" data-testid="exc-capture">
            {capture.medianCapturePct == null ? (
              // ⚠ NOT "0%". No qualifying trade means the question is unanswered.
              <p className="muted">
                No trade in this scope offered a full unit of risk in favourable
                excursion, so there is nothing to measure capture against.
                {capture.gatedOut ? ` ${capture.gatedOut} trade(s) fell below the 1R threshold.` : ''}
              </p>
            ) : (
              <>
                <div className="exc-big">{pct(capture.medianCapturePct, 0)}</div>
                {/* ⚠ The sample is stated beside the number. A median over 6 of
                    200 trades is a different claim from one over 190. */}
                <p className="muted">
                  Median over <b>{capture.sample}</b> trade{capture.sample === 1 ? '' : 's'} whose
                  favourable excursion reached 1R or more.
                  {capture.gatedOut ? ` ${capture.gatedOut} excluded below that threshold.` : ''}
                </p>
              </>
            )}
          </div>

          <HoldCard unit={unit} hold={hold} />
          <CostCard unit={unit} giveBack={giveBack} premature={premature} groupBy={groupBy} />
          <ExtensionCard
            profile={extension}
            window={extWindow} onWindow={setExtWindow}
            lens={extLens} onLens={setExtLens}
            mode={extMode} onMode={setExtMode}
            reason={extReason} onReason={setExtReason}
          />

          <div className="section-header exc-divider">
            <h3>Diagnose &amp; calibrate</h3>
            <span className="muted">where is it bleeding, and what do you change?</span>
          </div>
          <p className="muted exc-section-blurb">
            Was the loss an entry fault or an exit fault, and how much of the
            opportunity are you actually taking? Then resize the stop and the
            target to what your trades really do.
          </p>

          <AsymmetryCard result={asymmetry} groupBy={groupBy} />

          <div className="section-header">
            <h3>Stop &amp; target calibration</h3>
            <span className="muted">{calib.winners}W / {calib.losers}L in scope</span>
          </div>
          <div className="card" data-testid="exc-calibration">
            <dl className="kv">
              <dt className="muted">Winner MAE p75</dt>
              <dd>{fmtPts(calib.winnerMaeP75)}<span className="muted"> — what a stop has to clear</span></dd>
              <dt className="muted">Winner MFE p50</dt>
              <dd>{fmtPts(calib.winnerMfeP50)}<span className="muted"> — how far they typically reached</span></dd>
              <dt className="muted">Winner points p50</dt>
              <dd>{fmtPts(calib.winnerPointsP50)}<span className="muted"> — what they actually banked</span></dd>
              <dt className="muted">Loser MAE p50, in R</dt>
              <dd>
                <span className={calib.loserMaeRP50 != null && calib.loserMaeRP50 > 1 ? 'red' : ''}>
                  {calib.loserMaeRP50 == null ? '—' : `${num(calib.loserMaeRP50, 2)}R`}
                </span>
                {/* ⚠ The one figure to audit before any other fix, per the old
                    client's own copy: above 1.0 the stop was not honoured. */}
                {calib.loserMaeRP50 != null && calib.loserMaeRP50 > 1 ? (
                  <span className="red"> — above 1R: stops were not honoured</span>
                ) : <span className="muted"> — 1R or less means stops held</span>}
              </dd>
            </dl>

            {calib.winnerMfeP50 != null && calib.winnerPointsP50 != null ? (
              <p className="muted">
                Gap between what winners reached and what they banked:{' '}
                <b>{fmtPts(calib.winnerMfeP50 - calib.winnerPointsP50)}</b> — target left on the table.
              </p>
            ) : null}
          </div>

          <RMultipleCard profile={rMultiple} groupBy={groupBy} />
        </>
      )}

      {/* ⚠ RE-READ EVERY TIME SOMETHING SHIPS, and this is the third rewrite.
          It said six cards were missing when the real figure was nine, then
          three; all eleven are now here, so the honest remaining gaps are
          BEHAVIOURS, not cards. Naming a card that exists is how this copy went
          wrong twice — a "what's missing" note is the one piece of text in the
          product whose accuracy decreases as the work goes well. */}
      <div className="card">
        <h3>What these cards can’t do yet</h3>
        <p className="muted">
          All eleven excursion cards are on screen. Three things the old client
          does here are still missing, and none of them is a number:
        </p>
        <ul className="muted exc-note">
          <li>
            <b>Computing a post-exit walk.</b> Measuring what happened after an
            exit means fetching one-second bars from a paid data vendor, per
            trade. That runs in the old client, behind its Compute button; this
            one only reads what it left behind. A trade whose walk was never
            computed is absent from Trade Extension rather than counted as zero.
          </li>
          <li>
            <b>Clicking a row to open that setup’s rules</b>, and clicking a
            count to list the trades behind it.
          </li>
          <li>
            <b>The ? explainers</b> that sit beside eight of these card titles in
            the old client.
          </li>
        </ul>
      </div>
    </div>
  );
}

/** ⚠ Points, not money — and null renders as an em dash, never 0.00. */
const fmtPts = (v: number | null): string => (v == null ? '—' : `${num(v, 2)} pts`);

const groupNoun = (g: GroupBy): string => (g === 'pattern' ? 'patterns' : 'exit models');
const groupCol = (g: GroupBy): string => (g === 'pattern' ? 'Pattern' : 'Exit model');

// ---------------------------------------------------------------------------
// E-Ratio
// ---------------------------------------------------------------------------

/**
 * ⚠ `∞` IS A REAL READING, not a formatting failure. It means the group's
 * average adverse excursion is under a tenth of a point — it has effectively
 * never gone against you in this sample. Printing a five-figure ratio instead
 * would look like precision.
 */
const fmtE = (e: number): string => (Number.isFinite(e) ? num(e, 2) : '∞');

function EratioCard(
  { edge, groupBy, unit }:
  { edge: EratioResult | null; groupBy: GroupBy; unit: PnlUnit },
) {
  if (!edge) return null;
  return (
    <div className="card" data-testid="exc-eratio">
      <h3>E-Ratio — setup edge score</h3>
      {edge.rows.length === 0 ? (
        <p className="muted">
          No {groupNoun(groupBy)} with at least five trades carrying both an MAE
          and an MFE in this scope. Same data prerequisite as stop calibration
          below.
        </p>
      ) : (
        <>
          <div className="exc-tiles">
            <Tile label="Best edge" value={edge.best ? fmtE(edge.best.eratio) : '—'}
              sub={edge.best ? `${edge.best.name} · ${edge.best.sample} trades` : ''} />
            <Tile label="Worst edge" value={edge.worst ? fmtE(edge.worst.eratio) : '—'}
              sub={edge.worst ? `${edge.worst.name} · ${edge.worst.sample} trades` : ''} />
            <Tile label="Average" value={edge.averageEratio == null ? '—' : num(edge.averageEratio, 2)}
              sub={`across ${edge.rows.length} ${groupNoun(groupBy)}`} />
          </div>
          <table className="exc-table">
            <thead>
              <tr>
                <th>{groupCol(groupBy)}</th><th>n</th><th>Avg MFE</th><th>Avg MAE</th>
                <th>E-Ratio</th><th>Win %</th><th>Net P&amp;L</th>
              </tr>
            </thead>
            <tbody>
              {[...edge.rows].sort((a, b) => rankE(b.eratio) - rankE(a.eratio)).map((r) => (
                <tr key={r.id} data-verdict={r.verdict}>
                  <td>{r.name}</td>
                  <td className="mono">{r.sample}</td>
                  <td className="mono">{num(r.avgMfe, 2)}</td>
                  <td className="mono">{num(r.avgMae, 2)}</td>
                  <td className={`mono exc-e ${r.verdict}`}>{fmtE(r.eratio)}</td>
                  <td className="mono">{pct(r.winRatePct, 0)}</td>
                  <td className="mono">{unitCompact(r.unitPnl, unit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted exc-note">
            Average favourable excursion ÷ average adverse excursion. It asks
            whether the setup has room to run <i>regardless of how you exited</i>.
            <b> 1.5 or more</b> is a real edge · <b>1.0 to 1.5</b> is marginal ·
            <b> under 1.0</b> means even perfect exits cannot save it. Read it
            beside win rate: a high E-Ratio with a low win rate is an exit
            problem; a high win rate with a low E-Ratio is taking small wins.
          </p>
        </>
      )}
    </div>
  );
}

const rankE = (e: number): number => (Number.isFinite(e) ? e : 99);

// ---------------------------------------------------------------------------
// MAE / stop bands
// ---------------------------------------------------------------------------

function StopBandCard({ rows, groupBy }: { rows: MaeStopRow[] | null; groupBy: GroupBy }) {
  if (!rows) return null;
  return (
    <div className="card" data-testid="exc-stopbands">
      <h3>MAE vs stop distance — where winners actually hurt</h3>
      {rows.length === 0 ? (
        <p className="muted">
          No {groupNoun(groupBy)} with at least five <b>winners</b> carrying both
          an MAE and a planned stop in this scope.
        </p>
      ) : (
        <>
          <table className="exc-table">
            <thead>
              <tr>
                <th>{groupCol(groupBy)}</th><th>Winners</th><th>Median</th>
                <th className="exc-bar-h">Distribution across the stop</th><th>Read</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="mono">{r.sample}</td>
                  <td className="mono">{r.medianRatio == null ? '—' : pct(r.medianRatio * 100, 0)}</td>
                  <td>
                    <div className="exc-bands" role="img"
                      aria-label={MAE_STOP_BANDS.map((b, i) => `${b.label}: ${r.counts[i]}`).join(', ')}>
                      {r.shares.map((s, i) => (
                        s > 0 ? (
                          /* ⚠ THE BAND INDEX IS A DATA ATTRIBUTE, not part of the
                             class name. `exc-band b${i}` would put a COMPUTED
                             class on screen, and the style guard cannot verify
                             one — it read the literal fragment `b` and asked for
                             a rule behind it. Five colours keyed on
                             `[data-band]` leave the class name a literal the
                             guard can actually check. */
                          <span
                            key={MAE_STOP_BANDS[i]!.label}
                            className="exc-band"
                            data-band={i}
                            style={{ width: `${(s * 100).toFixed(1)}%` }}
                            title={`${MAE_STOP_BANDS[i]!.label} — ${r.counts[i]} winner(s)`}
                          />
                        ) : null
                      ))}
                    </div>
                  </td>
                  <td className={`exc-verdict ${r.verdict}`}>
                    {r.verdict === 'slack' ? 'Stop has slack'
                      : r.verdict === 'tight' ? 'Stops cut close' : 'Balanced'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted exc-note">
            Winners only — a loser either hit the stop or was cut before it, so it
            says nothing about whether the stop was well placed. Weight toward the
            <b> left</b> means winners barely tagged the stop, so tightening it
            buys size. Weight toward the <b>right</b> means they came close, so
            the stop is either well placed or you are entering early.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Temporal profile
// ---------------------------------------------------------------------------

function TempoCard({ rows, groupBy }: { rows: TemporalRow[] | null; groupBy: GroupBy }) {
  if (!rows) return null;
  return (
    <div className="card" data-testid="exc-tempo">
      <h3>Tempo — how fast it rips, how fast it fails</h3>
      {rows.length === 0 ? (
        <p className="muted">
          No {groupNoun(groupBy)} with at least five trades carrying both timings
          in this scope. Time-to-MFE and time-to-MAE come from the logger, so
          hand-entered trades will not appear here.
        </p>
      ) : (
        <>
          <table className="exc-table">
            <thead>
              <tr>
                <th>{groupCol(groupBy)}</th><th>n</th>
                <th>Time to peak</th><th>Med MFE</th>
                <th>Time to worst</th><th>Med MAE</th>
                <th>Read</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="mono">{r.sample}</td>
                  {/* ⚠ The two halves come from DIFFERENT trades — the peak from
                      winners, the worst from losers — so each is labelled. */}
                  <td className={`mono exc-tempo ${r.mfeTempo}`}>{durationLabel(r.medMfeSecs)}</td>
                  <td className="mono">{fmtPts(r.medMfePoints)}</td>
                  <td className={`mono exc-tempo ${r.maeTempo}`}>{durationLabel(r.medMaeSecs)}</td>
                  <td className="mono">{fmtPts(r.medMaePoints)}</td>
                  <td>{r.label}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted exc-note">
            <b>Time to peak</b> and <b>Med MFE</b> are measured on your{' '}
            <b>winners</b> — how the favourable move develops when it works.{' '}
            <b>Time to worst</b> and <b>Med MAE</b> are measured on your{' '}
            <b>losers</b> — how it fails when it doesn’t. Under two minutes is
            fast, over eight is slow. A fast peak suits quick partials; a slow one
            suits a runner. A fast failure means the invalidation is clean and the
            stop can be trusted; a slow one is a bleed.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hold-duration edge
// ---------------------------------------------------------------------------

function HoldCard({ hold, unit }: { hold: HoldDurationEdge | null; unit: PnlUnit }) {
  if (!hold) return null;
  return (
    <div className="card" data-testid="exc-hold">
      <h3>Hold-duration edge — where in the hold your edge lives</h3>
      {hold.sample === 0 ? (
        <p className="muted">
          No trade in this scope has both a favourable excursion and a readable
          entry and exit time.
        </p>
      ) : (
        <>
          <div className="exc-tiles">
            <Tile label="Median hold" value={`${num(hold.medianHoldMinutes ?? 0, 0)}m`}
              sub={`${hold.sample} trade${hold.sample === 1 ? '' : 's'} with a peak`} />
            <Tile label="Avg give-back" value={fmtPts(hold.avgGiveBackPoints)}
              sub="from the peak to what you banked" />
            {/* ⚠ ITS OWN SAMPLE, because the timing comes from the logger and
                covers fewer trades than the rest of the card. */}
            <Tile label="Median time to peak"
              value={hold.medianPeakSecs == null ? '—' : durationLabel(hold.medianPeakSecs)}
              sub={hold.peakSample
                ? `${hold.peakSample} with tick timing`
                : 'needs the logger’s tick timing'} />
          </div>
          <table className="exc-table">
            <thead>
              <tr>
                <th>Hold</th><th>n</th><th>Avg P&amp;L</th><th>Avg give-back</th>
                <th>Winner capture</th><th>Round-trips</th><th>Win %</th>
              </tr>
            </thead>
            <tbody>
              {hold.rows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td className="mono">{r.n || '—'}</td>
                  <td className={`mono ${r.avgUnitPnl == null ? '' : signClass(r.avgUnitPnl)}`}>
                    {unitCompact(r.avgUnitPnl, unit)}
                  </td>
                  <td className="mono">{fmtPts(r.avgGiveBackPoints)}</td>
                  <td className="mono">{r.winnerCapturePct == null ? '—' : pct(r.winnerCapturePct, 0)}</td>
                  <td className="mono">{r.roundTripPct == null ? '—' : pct(r.roundTripPct, 0)}</td>
                  <td className="mono">{r.winRatePct == null ? '—' : pct(r.winRatePct, 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {hold.best && hold.worst && hold.best.label !== hold.worst.label ? (
            <p className="muted exc-note">
              Your best hold band is <b>{hold.best.label}</b> and your worst is{' '}
              <b>{hold.worst.label}</b>.
            </p>
          ) : null}
          <p className="muted exc-note">
            <b>Winner capture</b> is the median share of the offered move kept on
            trades that closed green, so it stays between 0 and 100%.{' '}
            <b>Round-trips</b> counts separately: trades that offered a full point
            in your favour and still closed red. They are not folded into capture
            as a negative — they are the give-back habit, and they deserve their
            own number.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two costs, side by side
// ---------------------------------------------------------------------------

/**
 * ⚠ ONE CARD FOR BOTH, deliberately. They are opposite habits — exiting too late
 * and exiting too early — and the value of either is knowing which dominates.
 * Two separate cards invite reading whichever is on screen as "the" problem.
 */
function CostCard(
  { giveBack, premature, groupBy, unit }: {
    giveBack: GiveBackCost | null; premature: PrematureExitCost | null; groupBy: GroupBy;
    unit: PnlUnit;
  },
) {
  if (!giveBack || !premature) return null;
  const habit = dominantExitHabit(giveBack, premature);

  return (
    <div className="card" data-testid="exc-cost">
      <h3>Cost of give-back vs cost of leaving early</h3>

      <div className="exc-tiles">
        {/* ⚠ `≈` WHEN ANY TRADE COULD NOT BE RECONSTRUCTED, which is what beta
            does. Give-back is measured by replaying the fills to find what was
            actually on at the peak; a trade with no fill timeline falls back to
            "the whole final position was on", which is an upper bound. Printing
            a bound as a measurement is the defect #88 filed — the number was
            five times the truth on a three-lot scale-out — so the reader is told
            when part of the total is one. */}
        {/* ⚠ "THAT GAVE SOME BACK", NOT "WITH A PEAK", and the wording had to
            move with the population. A trade that exited at or past its peak
            gave nothing back and is no longer counted — so a label saying "with
            a peak" would now describe a different set than the one being
            counted. That is how a number and its caption drift apart. */}
        <Tile label="Given back"
          value={`${giveBack.approx ? '≈' : ''}${unitCompact(giveBack.total, unit)}`}
          sub={giveBack.sample
            ? `over ${giveBack.sample} trade${giveBack.sample === 1 ? '' : 's'} that gave some back${
              giveBack.approx ? ` · ${giveBack.approx} estimated from final size` : ''}`
            : 'no trade gave anything back'} />
        <Tile label="Left on the table" value={unitCompact(premature.net, unit)}
          sub={premature.sample
            ? `over ${premature.sample} trade${premature.sample === 1 ? '' : 's'} with a stored run`
            : 'no trade has a stored post-exit run'} />
        <Tile label="Protective exits"
          value={premature.sample ? pct((100 * premature.stopped) / premature.sample, 0) : '—'}
          sub="would have hit your original stop" />
      </div>

      {habit ? (
        <p className="exc-habit">
          {habit === 'give-back'
            ? <>Your dominant habit is <b>holding too long</b> — you give back more
              from the peak than you leave behind by exiting.</>
            : habit === 'premature'
              ? <>Your dominant habit is <b>leaving too early</b> — the move continues
                after you are flat more than you hand back from the peak.</>
              : <>Neither habit dominates: what you hand back from the peak and what
                you leave behind after exiting are close to even.</>}
        </p>
      ) : (
        <p className="muted exc-note">
          {/* ⚠ NOT "balanced". One side has no population, and reporting that as
              an even split would be an answer to a question nobody could ask. */}
          Only one of the two can be measured in this scope, so there is nothing
          to compare yet.
        </p>
      )}

      <div className="exc-split">
        <div>
          <div className="exc-split-h">Given back, by outcome</div>
          <dl className="kv">
            <dt className="muted">Still closed green</dt>
            <dd>{unitCompact(giveBack.winnerCost, unit)}
              <span className="muted"> · {giveBack.winnerCount} trade{giveBack.winnerCount === 1 ? '' : 's'}</span>
            </dd>
            <dt className="muted">Round-tripped into a loss</dt>
            <dd className="red">{unitCompact(giveBack.loserCost, unit)}
              <span className="muted"> · {giveBack.loserCount} trade{giveBack.loserCount === 1 ? '' : 's'}</span>
            </dd>
          </dl>
          {giveBack.unpriced ? (
            <p className="muted exc-note">
              {giveBack.unpriced} trade{giveBack.unpriced === 1 ? '' : 's'} could not
              be priced — no dollars-per-point for that contract — so {giveBack.unpriced === 1 ? 'it is' : 'they are'} not
              in the figure above.
            </p>
          ) : null}
        </div>
        <div>
          <div className="exc-split-h">Left behind, by {groupBy === 'pattern' ? 'pattern' : 'exit model'}</div>
          {premature.rows.length === 0 ? (
            <p className="muted">
              No {groupNoun(groupBy)} with at least five trades carrying a stored
              post-exit run. The headline above still counts every one of them.
            </p>
          ) : (
            <table className="exc-table">
              <thead>
                <tr><th>{groupCol(groupBy)}</th><th>n</th><th>Net</th><th>Kept running</th><th>Med run</th></tr>
              </thead>
              <tbody>
                {[...premature.rows].sort((a, b) => b.net - a.net).map((r) => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td className="mono">{r.sample}</td>
                    <td className={`mono ${signClass(r.net)}`}>{unitCompact(r.net, unit)}</td>
                    <td className="mono">{pct(r.keptPct, 0)}</td>
                    <td className="mono">{fmtPts(r.medianPoints)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <p className="muted exc-note">
        <b>Given back</b> is the gap between a trade’s best price <i>while you
        held it</i> and what you banked — always a cost. It replays your fills to
        find what was actually still on when the high printed, so a scale-out is
        not charged for size you had already booked. Where a trade’s fills cannot
        be reconstructed the figure falls back to assuming the whole position was
        on, which is an upper bound — those totals are marked{' '}
        <span className="mono">≈</span>.{' '}
        <b>Left on the table</b> is the opposite: how far it kept running{' '}
        <i>after</i> you were flat. That one is signed — a trade that ran and then
        reversed into where your stop was would have <i>cost</i> you to hold, so
        it counts against the total rather than being quietly dropped.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trade extension — the shape of the post-exit run
// ---------------------------------------------------------------------------

/**
 * ⚠ THIS CARD OFFERS NO COMPUTE BUTTON, and that is the honest shape rather than
 * a missing feature. Walking the seconds after an exit costs money at a metered
 * vendor; it runs in the old client. Everything here reads what that left behind,
 * so a trade with no stored walk is ABSENT — never a zero, which would read as
 * "nothing kept running after you left".
 */
function ExtensionCard(
  { profile, window: win, onWindow, lens, onLens, mode, onMode, reason, onReason }: {
    profile: ExtensionProfile | null;
    window: string; onWindow: (w: string) => void;
    lens: ExtensionLens; onLens: (l: ExtensionLens) => void;
    mode: ExtensionMode; onMode: (m: ExtensionMode) => void;
    reason: ExitReasonKey | null; onReason: (r: ExitReasonKey | null) => void;
  },
) {
  if (!profile) return null;
  const isPts = mode === 'points';
  /**
   * ⚠ NO SIGN ON A ZERO, in either direction. The old client prints `+0.00R` for
   * a setup whose median post-exit run was flat, which reads as a small win; the
   * same expression on a value that rounds to zero from below gives `-0.00R`,
   * which this product has already shipped once (PD-3's family). A flat run is
   * flat.
   */
  const fmtV = (v: number | null): string => {
    if (v == null) return '—';
    const s = num(v, 2);
    const flat = Number(s.replace(/,/g, '')) === 0;
    const body = flat ? s.replace(/^-/, '') : s;
    return `${!flat && v > 0 ? '+' : ''}${body}${isPts ? ' pts' : 'R'}`;
  };
  const windowLabel = EXTENSION_WINDOWS.find((w) => w.key === win)?.label ?? win;
  const withData = EXTENSION_WINDOWS.filter((w) => (profile.windowCounts[w.key] ?? 0) > 0);
  const stopPhrase = lens === 'be' ? 'price came back to break-even' : 'your stop would have been hit';

  const controls = (
    <>
      <div className="exc-ext-controls">
        <span className="exc-ext-l">Window</span>
        {EXTENSION_WINDOWS.map((w) => (
          <button
            key={w.key} type="button"
            className={`period-chip${win === w.key ? ' active' : ''}`}
            aria-pressed={win === w.key}
            onClick={() => onWindow(w.key)}
          >{w.label}</button>
        ))}
      </div>
      <div className="exc-ext-controls">
        <span className="exc-ext-l">Qualified against</span>
        <button
          type="button" className={`period-chip${lens === 'stop' ? ' active' : ''}`}
          aria-pressed={lens === 'stop'} onClick={() => onLens('stop')}
        >Your original stop</button>
        <button
          type="button" className={`period-chip${lens === 'be' ? ' active' : ''}`}
          aria-pressed={lens === 'be'} onClick={() => onLens('be')}
        >Break-even</button>
        <span className="muted exc-ext-hint">
          {lens === 'be'
            ? 'as if you had moved the stop to your entry'
            : 'as if you had held the stop you actually set'}
        </span>
      </div>
      <div className="exc-ext-controls">
        <span className="exc-ext-l">Show as</span>
        <button
          type="button" className={`period-chip${!isPts ? ' active' : ''}`}
          aria-pressed={!isPts} onClick={() => onMode('r')}
        >Multiples of risk</button>
        <button
          type="button" className={`period-chip${isPts ? ' active' : ''}`}
          aria-pressed={isPts} onClick={() => onMode('points')}
        >Points</button>
        <span className="muted exc-ext-hint">
          {isPts
            ? 'raw distance — includes trades that had no planned risk'
            : 'normalised to each trade’s own risk'}
        </span>
      </div>
    </>
  );

  return (
    <div className="card" data-testid="exc-extension">
      <h3>Trade extension — how far it kept going after you left</h3>

      {controls}

      {profile.computed === 0 ? (
        // ⚠ NOT an empty chart. Says which window has walks rather than leaving
        // the trader to click all four to find out.
        <p className="muted">
          No trade in this scope has a stored post-exit walk for the{' '}
          <b>{windowLabel}</b> window.{' '}
          {withData.length
            ? <>There {withData.length === 1 ? 'is one' : 'are walks'} for{' '}
              <b>{withData.map((w) => `${w.label} (${profile.windowCounts[w.key]})`).join(', ')}</b>.</>
            : <>None of these {profile.withoutWalk} trades has one for any window — they
              are computed in the old client, one paid bar fetch at a time.</>}
        </p>
      ) : (
        <>
          {isPts && profile.instruments > 1 ? (
            <p className="exc-warn">
              Points are being blended across <b>{profile.instruments} instruments</b> here —
              a point of ES is not a point of MNQ. Pick one instrument above, or
              switch to multiples of risk.
            </p>
          ) : null}

          <div className="exc-ext-controls">
            <span className="exc-ext-l">Exit</span>
            <button
              type="button" className={`period-chip${reason == null ? ' active' : ''}`}
              aria-pressed={reason == null} onClick={() => onReason(null)}
            >All ({profile.computed})</button>
            {/* ⚠ A reason with no trades is not offered. The old client's own
                classifier can never return "time", so a Time (0) chip would sit
                there for ever advertising a category that cannot exist. */}
            {(Object.keys(EXIT_REASON_LABELS) as ExitReasonKey[])
              .filter((k) => (profile.reasonCounts[k] ?? 0) > 0)
              .map((k) => (
                <button
                  key={k} type="button"
                  className={`period-chip${reason === k ? ' active' : ''}`}
                  aria-pressed={reason === k}
                  onClick={() => onReason(k)}
                >{EXIT_REASON_LABELS[k]} ({profile.reasonCounts[k]})</button>
              ))}
            <span className="muted exc-ext-hint">{windowLabel} after the exit</span>
          </div>

          {profile.excluded > 0 ? (
            <p className="exc-warn">
              <b>{profile.excluded} trade{profile.excluded === 1 ? '' : 's'}</b>{' '}
              {profile.excluded === 1 ? 'is' : 'are'} not counted below — this card
              never shows a run that nothing would have stopped.{' '}
              {profile.excludedShowableInPoints > 0
                ? <>Showing {profile.excludedShowableInPoints === profile.excluded ? 'them' : `${profile.excludedShowableInPoints} of them`} in{' '}
                  <b>points</b> works, because there is no risk to divide by there.</>
                : <>No view can show {profile.excluded === 1 ? 'it' : 'them'}: the walk
                  had neither a stop nor an entry to measure against.</>}
            </p>
          ) : null}

          {profile.shown === 0 ? (
            <p className="muted">
              Every walk in this view was excluded, so there is nothing to
              measure. Try points, or break-even.
            </p>
          ) : (
            <>
              <div className="exc-tiles">
                <Tile
                  label="Typical run after your exit"
                  value={fmtV(profile.medianValue)}
                  sub={`${profile.dollarSample
                    ? `${money(profile.medianDollars, 0)} typical · ` : ''}${profile.shown} trade${profile.shown === 1 ? '' : 's'}`}
                />
                <Tile
                  label={isPts ? 'Kept running' : 'Ran another full unit of risk'}
                  value={pct(profile.goodPct, 0)}
                  sub={`${profile.goodCount} of ${profile.shown}`}
                />
                {/* ⚠ Target exits are the interesting case: even the exits that
                    went exactly to plan usually had more behind them. */}
                <Tile
                  label="On your target exits"
                  value={fmtV(profile.medianTargetValue)}
                  sub={profile.targetSample
                    ? `${profile.targetSample} exit${profile.targetSample === 1 ? '' : 's'} that hit the target`
                    : 'no target exits in this view'}
                />
              </div>

              <ExtensionHistogram profile={profile} />

              {/* 🚨 THE TWO SHARES OVERLAP, AND THIS SAYS SO. The old client's
                  sentence here reads "40% ran 1R or more WITHOUT hitting your
                  stop first, while 67% would've stopped out before extending
                  (not counted)". Both halves are false — the walk ends AT the
                  stop, so a stopped trade still contributes the run it made
                  before it. On Josh's own MES book 20 trades are in both, out of
                  146, and subtracting as that sentence invites gives a figure
                  that describes nothing. PD-14. */}
              <p className="exc-habit">
                The typical trade kept moving <b>{fmtV(profile.medianValue)}</b> your
                way after you were flat
                {profile.medianValue != null && profile.medianValue < 0
                  ? ' — it turned against you, so those exits were well timed' : ''}
                . <b>{pct(profile.goodPct, 0)}</b>{' '}
                {isPts ? 'kept running' : 'ran another full unit of risk'} before
                anything would have taken you out.
                {profile.stoppedFirstCount > 0 ? (
                  <> Separately, <b>{pct(profile.stoppedFirstPct, 0)}</b> would have been
                    stopped out somewhere inside the {windowLabel} — for those, the
                    figure above is only the part of the move that came before{' '}
                    {stopPhrase}, not the whole run.
                    {profile.goodAndStoppedCount > 0 ? (
                      <> <b>{profile.goodAndStoppedCount}</b> trade
                        {profile.goodAndStoppedCount === 1 ? '' : 's'} are in both groups,
                        so the two shares do not add up to your book.</>
                    ) : null}
                  </>
                ) : null}
              </p>

              {profile.patternRows.length ? (
                <>
                  <div className="exc-split-h">Where you leave the most behind</div>
                  <table className="exc-table">
                    <thead>
                      <tr>
                        <th>Setup</th><th>n</th><th>Typical run</th>
                        <th>{isPts ? 'Kept running' : 'Ran 1R+'}</th><th>Sample</th>
                      </tr>
                    </thead>
                    <tbody>
                      {profile.patternRows.map((r) => (
                        <tr key={r.id} className={r.sample < 5 ? 'exc-dim' : undefined}>
                          <td>{r.name}</td>
                          <td className="mono">{r.sample}</td>
                          <td className={`mono ${r.medianValue == null ? '' : signClass(r.medianValue)}`}>
                            {fmtV(r.medianValue)}
                          </td>
                          <td className="mono">{pct(r.keptPct, 0)}</td>
                          <td className="exc-verdict">
                            {r.sample < 5 ? `too few — ${5 - r.sample} more`
                              : r.sample >= 20 ? 'reliable' : 'limited'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : null}

              {profile.phases.length >= 2 ? (
                <>
                  <div className="exc-split-h">By time of day you exited</div>
                  {profile.phases.map((p) => (
                    <div className="exc-phase" key={p.key}>
                      <div className="exc-phase-l">{p.label}</div>
                      <div className="exc-phase-bar">
                        <span
                          className="exc-phase-fill"
                          data-sign={(p.medianValue ?? 0) >= 0 ? 'up' : 'down'}
                          style={{ width: `${phaseWidth(p.medianValue, profile.phases)}%` }}
                        />
                      </div>
                      <div className={`exc-phase-v mono ${signClass(p.medianValue ?? 0)}`}>
                        {fmtV(p.medianValue)} · {p.sample}
                      </div>
                    </div>
                  ))}
                </>
              ) : null}
            </>
          )}
        </>
      )}

      <p className="muted exc-note">
        This is the move that came <i>after</i> you were out — the mirror of the
        give-back card above, which measures the peak <i>while you held it</i>. A
        run only counts if you would still have been in the trade to see it: the
        walk stops at the first price that would have taken you out. That is why a
        big number here means you left early, and a negative one means the market
        turned right after you did.
        {profile.withoutWalk > 0 ? (
          <> {profile.withoutWalk} trade{profile.withoutWalk === 1 ? '' : 's'} in
            this scope {profile.withoutWalk === 1 ? 'has' : 'have'} no stored walk
            at all and {profile.withoutWalk === 1 ? 'is' : 'are'} left out
            entirely rather than counted as nothing.</>
        ) : null}
      </p>
    </div>
  );
}

/** Longest bar is the biggest magnitude, so the phases are comparable to each other. */
function phaseWidth(v: number | null, all: readonly { medianValue: number | null }[]): number {
  const max = Math.max(0.5, ...all.map((p) => Math.abs(p.medianValue ?? 0)));
  return Math.min(100, (Math.abs(v ?? 0) / max) * 100);
}

/**
 * ⚠ SEVEN BUCKETS, THREE OF THEM NEGATIVE, in both display modes. The point
 * ladder is derived from the data because a point has no universal scale, but it
 * keeps the same shape as the fixed risk-multiple one — so the red/green split
 * lands in the same place and the two modes read as the same chart.
 */
function ExtensionHistogram({ profile }: { profile: ExtensionProfile }) {
  const max = Math.max(1, ...profile.buckets.map((b) => b.count));
  const barW = 90; const gap = 10;
  const width = profile.buckets.length * (barW + gap) - gap;
  return (
    <div className="exc-hist">
      <svg viewBox={`0 0 ${width} 148`} preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={profile.buckets.map((b) => `${b.label}: ${b.count}`).join(', ')}>
        {profile.buckets.map((b, i) => {
          const h = (b.count / max) * 110;
          const y = 130 - h;
          const up = i >= EXTENSION_POSITIVE_FROM;
          return (
            <g key={b.label}>
              <rect
                x={i * (barW + gap)} y={y} width={barW} height={h}
                fill={up ? 'var(--green)' : 'var(--red)'}
                opacity={up ? 0.3 + (i - EXTENSION_POSITIVE_FROM) * 0.07 : 0.3 + (EXTENSION_POSITIVE_FROM - i) * 0.1}
              />
              {/* ⚠ Paint and SIZE on the element — see `svgSnapshotSafe.test.ts`.
                  A snapshot rasteriser drops class rules on SVG children, and
                  losing `font-size` here would blow these labels up to the 16px
                  default and overlap every bar. Latent today (this tab has no
                  snapshot button); fixed with the chart that proved it. */}
              <text x={i * (barW + gap) + barW / 2} y={y - 6} textAnchor="middle"
                fill="var(--text-2)" fontSize={10}
                style={{ fontVariantNumeric: 'tabular-nums' }}>{b.count}</text>
              <text x={i * (barW + gap) + barW / 2} y={140} textAnchor="middle"
                fill="var(--text-3)" fontSize={9}
                style={{ fontVariantNumeric: 'tabular-nums' }}>{b.label}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loser asymmetry
// ---------------------------------------------------------------------------

const ASYM_WORDS: Record<string, string> = {
  entry: 'Entry problem',
  exit: 'Exit problem',
  'no-follow-through': 'No follow-through',
  mixed: 'Mixed',
};

function AsymmetryCard({ result, groupBy }: { result: LoserAsymmetry | null; groupBy: GroupBy }) {
  if (!result) return null;
  const noun = groupNoun(groupBy);
  const one = groupBy === 'pattern' ? 'setup' : 'exit model';

  return (
    <div className="card" data-testid="exc-asymmetry">
      <h3>Loser asymmetry — was it the entry or the exit?</h3>

      {result.rows.length === 0 ? (
        // ⚠ THE COUNTS ARE THE POINT. "No pattern met the threshold" tells the
        // trader nothing they can act on; these four numbers tell them whether to
        // tag their trades, or that their older imports predate tick timing.
        <>
          <p className="muted">
            Needs at least {ASYMMETRY_MIN_LOSERS} losing trades on one {one} that
            carry both the logger’s timings <i>and</i> a {one} tag.
          </p>
          <p className="muted">
            In this scope: <b>{result.totalLosers}</b> loser{result.totalLosers === 1 ? '' : 's'} ·{' '}
            <b>{result.withTiming}</b> with timings · <b>{result.withGroup}</b> tagged ·{' '}
            <b>{result.withBoth}</b> with both.
          </p>
          <p className="muted exc-note">
            {result.withTiming === 0
              ? <>None carries the timings. Those come from the logger — trades
                imported from a broker file record how far the trade went, but not
                when, so they cannot be placed in order here.</>
              : result.withBoth === 0
                ? <>Some carry the timings, but none of those is tagged. Tag them
                  with a {one} on the trade form and they will appear.</>
                : <>Enough carry both, but they are spread across too many {noun} for
                  any one to reach {ASYMMETRY_MIN_LOSERS}.</>}
          </p>
        </>
      ) : (
        <>
          <div className="exc-tiles">
            <Tile label={`${groupBy === 'pattern' ? 'Setups' : 'Exit models'} measured`}
              value={String(result.rows.length)}
              sub={`${ASYMMETRY_MIN_LOSERS}+ losers with timings each`} />
            <Tile label="Handing winners back"
              value={result.mostExitBroken?.name ?? '—'}
              sub={result.mostExitBroken
                ? `${pct(result.mostExitBroken.gaveBackPct ?? 0, 0)} of its losers were real winners first`
                : 'none stands out'} />
            <Tile label="Wrong from the first tick"
              value={result.mostEntryBroken?.name ?? '—'}
              sub={result.mostEntryBroken
                ? `${pct(100 - result.mostEntryBroken.favoredPct, 0)} went straight against you`
                : 'none stands out'} />
          </div>

          {/* ⚠ EIGHT COLUMNS, and the last one is the verdict. Squeezed, it
              truncates to "Entry proble…" — the one thing the card is for. */}
          <div className="exc-scroll">
          <table className="exc-table">
            <thead>
              <tr>
                <th>{groupCol(groupBy)}</th><th>Losers</th>
                <th>Went your way first</th><th>Typical reach</th>
                <th>Was a real winner</th>
                <th>Time to peak</th><th>Time to worst</th><th>Read</th>
              </tr>
            </thead>
            <tbody>
              {[...result.rows].sort((a, b) => a.favoredPct - b.favoredPct).map((r) => (
                <tr key={r.id}>
                  <td>{r.name}{r.losers < 5
                    ? <span className="muted"> · small sample</span> : null}</td>
                  <td className="mono">{r.losers}
                    <span className="muted"> ({r.favoredFirst}/{r.adverseFirst})</span>
                  </td>
                  <td className={`mono exc-asym ${r.verdict}`}>{pct(r.favoredPct, 0)}</td>
                  <td className="mono">{r.medMfeR == null ? '—' : `${num(r.medMfeR, 2)}R`}</td>
                  {/* ⚠ AN EM DASH, NOT "0%", when no loser carried a planned
                      stop. The old client prints 0% there — a claim about a
                      population it could not measure. PD-15. */}
                  <td className="mono">
                    {r.gaveBackPct == null ? '—' : pct(r.gaveBackPct, 0)}
                    {r.gaveBackPct != null && r.rSample < r.losers
                      ? <span className="muted"> of {r.rSample}</span> : null}
                  </td>
                  <td className="mono">{durationLabel(r.medMfeSecs)}</td>
                  <td className="mono">{durationLabel(r.medMaeSecs)}</td>
                  <td className={`exc-asym ${r.verdict}`}>{ASYM_WORDS[r.verdict]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {result.mostExitBroken ? (
            <p className="exc-habit">
              <b>{result.mostExitBroken.name}</b> is costing you at the exit —{' '}
              <b>{pct(result.mostExitBroken.gaveBackPct ?? 0, 0)}</b> of its losers had
              already gone a full unit of risk your way before turning, typically
              reaching {num(result.mostExitBroken.medMfeR, 2)}R after{' '}
              {durationLabel(result.mostExitBroken.medMfeSecs)}. The entry works;
              you are handing real winners back.
            </p>
          ) : null}
          {result.mostEntryBroken ? (
            <p className="exc-habit">
              <b>{result.mostEntryBroken.name}</b> is going wrong at the entry —{' '}
              <b>{pct(100 - result.mostEntryBroken.favoredPct, 0)}</b> of its losers
              never went your way at all, reaching their worst point after{' '}
              {durationLabel(result.mostEntryBroken.medMaeSecs)}. That is the trigger
              or the filter, not the exit.
            </p>
          ) : null}

          <p className="muted exc-note">
            For each losing trade: did it go your way first, or straight against
            you? Going your way first means the entry was right and the hold or
            the exit lost it. Straight against means the trade was wrong from tick
            one. <b>Was a real winner</b> is the honest half — a loser that only
            drifted a fraction of your risk into profit was never a winner to hand
            back, and a {one} that does that has a follow-through problem rather
            than an exit problem. Both timings come from the logger, so
            hand-entered trades do not appear.
          </p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// R-multiple at MFE / MAE
// ---------------------------------------------------------------------------

const CAPTURE_WORDS: Record<string, string> = {
  'stops-not-honoured': 'Stop was breached',
  'big-gap': 'Big capture gap',
  'moderate-gap': 'Some capture gap',
  solid: 'Capture is solid',
  unknown: 'Not enough to say',
};

function RMultipleCard({ profile, groupBy }: { profile: RMultipleProfile | null; groupBy: GroupBy }) {
  if (!profile) return null;
  const one = groupBy === 'pattern' ? 'setup' : 'exit model';

  return (
    <div className="card" data-testid="exc-rmultiple">
      <h3>Opportunity size — how much of it you take</h3>

      {profile.rows.length === 0 ? (
        <p className="muted">
          No {groupNoun(groupBy)} with at least {RMULT_MIN_WINNERS} winning trades
          carrying both excursions <i>and</i> a planned risk. Everything here is
          measured in multiples of the risk you set, so a trade without a stop
          cannot be placed on the scale at all — it is left out rather than given
          a risk it never had.
        </p>
      ) : (
        <>
          <div className="exc-tiles">
            <Tile label="Typical winner offered"
              value={profile.overallMfeR == null ? '—' : `${num(profile.overallMfeR, 2)}R`}
              sub={`across ${profile.mfeSample} winner${profile.mfeSample === 1 ? '' : 's'}`} />
            {/* ⚠ ITS OWN SAMPLE beside it. The two medians are over different
                populations whenever a winner has no stored R, and the capture
                figure below divides one by the other. */}
            <Tile label="Typical winner banked"
              value={profile.overallRealizedR == null ? '—' : `${num(profile.overallRealizedR, 2)}R`}
              sub={`across ${profile.realizedSample} winner${profile.realizedSample === 1 ? '' : 's'}`} />
            <Tile label="So you keep"
              value={profile.overallCapture == null ? '—' : pct(profile.overallCapture * 100, 0)}
              sub="of what the setup offered" />
          </div>

          <div className="exc-scroll">
          <table className="exc-table">
            <thead>
              <tr>
                <th>{groupCol(groupBy)}</th><th>W / L</th>
                <th>Offered</th><th>Banked</th><th>Kept</th>
                <th>Loser’s worst</th><th>Read</th>
              </tr>
            </thead>
            <tbody>
              {[...profile.rows].sort((a, b) => (a.capture ?? 99) - (b.capture ?? 99)).map((r) => (
                <tr key={r.id}>
                  <td>{r.name}{r.winners < 10
                    ? <span className="muted"> · small sample</span> : null}</td>
                  <td className="mono">{r.winners}<span className="muted"> / {r.losers}</span></td>
                  <td className="mono">{r.medMfeR == null ? '—' : `${num(r.medMfeR, 2)}R`}</td>
                  <td className="mono">
                    {r.medRealizedR == null ? '—' : `${num(r.medRealizedR, 2)}R`}
                    {r.realizedSample < r.winners
                      ? <span className="muted"> of {r.realizedSample}</span> : null}
                  </td>
                  <td className={`mono exc-capture ${captureBand(r.capture)}`}>
                    {r.capture == null ? '—' : pct(r.capture * 100, 0)}
                  </td>
                  <td className={`mono ${r.medLoserMaeR != null && r.medLoserMaeR > 1 ? 'red' : ''}`}>
                    {r.medLoserMaeR == null ? '—' : `${num(r.medLoserMaeR, 2)}R`}
                  </td>
                  <td className={`exc-capture ${captureBand(r.capture, r.verdict)}`}>
                    {CAPTURE_WORDS[r.verdict]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* ⚠ THE BREACHED STOP GOES FIRST, above the capture gap. Every other
              number on that row is measured against a risk the trader did not
              actually take, so tuning exits there is work on a false premise. */}
          {profile.worstStops ? (
            <p className="exc-habit">
              <b>{profile.worstStops.name}</b> losers typically ran{' '}
              <b>{num(profile.worstStops.medLoserMaeR, 2)} times your planned risk</b>{' '}
              against you — past the stop you set. Either the stop is not being
              honoured or it is being moved mid-trade. Fix that before touching
              anything else on this {one}: every other figure on its row assumes
              the risk you planned.
            </p>
          ) : null}
          {profile.worstCapture && (profile.worstCapture.capture ?? 1) < 0.6 ? (
            <p className="exc-habit">
              <b>{profile.worstCapture.name}</b> has the biggest gap — the typical
              winner runs to <b>{num(profile.worstCapture.medMfeR, 2)}R</b> and you
              bank <b>{num(profile.worstCapture.medRealizedR, 2)}R</b>, so you keep{' '}
              {pct((profile.worstCapture.capture ?? 0) * 100, 0)} of it. That is a
              gap of {num((profile.worstCapture.medMfeR ?? 0) - (profile.worstCapture.medRealizedR ?? 0), 2)}R
              on every winner. The setup is producing; the exit is not collecting.
            </p>
          ) : null}

          <p className="muted exc-note">
            Everything here is in multiples of the risk you planned, which is the
            one measure that compares across instruments — 2.8R on MES and 2.8R on
            CL mean the same thing where 2.8 points do not. <b>Offered</b> is how
            far the typical winner reached, <b>banked</b> is what it actually
            made, and <b>kept</b> is the second divided by the first.{' '}
            <b>Loser’s worst</b> above 1.0 means the losers went further against
            you than the risk you set — audit that before anything else.
          </p>
        </>
      )}
    </div>
  );
}

/** ⚠ A literal class name per band, never `` `band-${n}` `` — the style guard can
 *  only verify a class it can read in the source. */
function captureBand(c: number | null, verdict?: string): string {
  if (verdict === 'stops-not-honoured') return 'bad';
  if (c == null) return 'unknown';
  if (c >= 0.7) return 'good';
  if (c >= 0.5) return 'fair';
  return 'bad';
}

/** ⚠ Zero is neither green nor red. PD-3 lives in exactly this helper. */
function signClass(n: number): string {
  if (n > 0) return 'green';
  if (n < 0) return 'red';
  return '';
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="exc-tile">
      <div className="exc-tile-l">{label}</div>
      <div className="exc-tile-v">{value}</div>
      <div className="exc-tile-s">{sub}</div>
    </div>
  );
}
